import React, { useEffect, useState, Suspense, lazy } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

// Standard imports for layout (always rendered)
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from './components/ScrollToTop';
import PasswordPage from './pages/PasswordPage';
import logo from './assets/Logo.png';
import staticData from './data.json';
import './fonts.css';

// Lazy-loaded routes (only downloaded when visited)
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./components/Aboutus'));
const Contactus = lazy(() => import('./components/Contactus'));
const CategoriesList = lazy(() => import('./components/CategoriesList'));
const CategoryPage = lazy(() => import('./components/ProductsPage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TestingComp = lazy(() => import('./components/TestingComp'));

function App() {
  const [isArabic, setIsArabic] = useState(false);
  const [products, setProducts] = useState([]);
  const [contact, setContact] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessGranted, setAccessGranted] = useState(true);

  // 1. OPTIMIZATION: Parallel Data Fetching
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch everything concurrently instead of sequentially
        const [brandsRes, contactRes, eventsRes] = await Promise.all([
          axios.get('https://ramsisindustry.com/api/brands/'),
          axios.get('https://ramsisindustry.com/api/contact/'),
          axios.get('https://ramsisindustry.com/api/events/')
        ]);

        setProducts(brandsRes.data);
        setContact(contactRes.data);
        setEvents(eventsRes.data);

      } catch (err) {
        console.error(err);
        setError(err);
        // Fallback to static data on error
        setProducts(staticData.brands);
        setContact(staticData.contact);
        setEvents(staticData.events);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  if (!accessGranted) {
    return <PasswordPage onAccessGranted={() => setAccessGranted(true)} />;
  }

  // Loading Screen Component
  const LoadingScreen = () => (
    <div className='flex justify-center items-center h-screen w-screen'>
      <img className='w-[100px] animate-ping' src={logo} alt="loading" />
    </div>
  );

  if (loading) return <LoadingScreen />;

  return (
    <Router>
      <ScrollToTop/>
      <div className="bg-white font-Roboto w-[100%] overflow-hidden roboto-black">
        <Navbar productsData={products} isArabic={isArabic} setIsArabic={setIsArabic}/>
        
        {/* 2. OPTIMIZATION: Suspense wrapper for lazy loading */}
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<Home isArabic={isArabic} events={events} />} />
            <Route path="/about" element={<About isArabic={isArabic}/>} />
            <Route path="/contact" element={<Contactus contact={contact} isArabic={isArabic}/>} />
            <Route path="/categories" element={<CategoriesList products={products} isArabic={isArabic}/>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy isArabic={isArabic}/>} />
            <Route path="/category/:categoryId/:categoryName" element={<CategoryPage productsData={products} isArabic={isArabic} />} />
            <Route path="/test" element={<TestingComp />} />
          </Routes>
        </Suspense>

        <Footer contact={contact} isArabic={isArabic}/>
      </div>
    </Router>
  );
}

export default App;
